import { Link } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { useFeaturedParks } from '../hooks/useApi';
import type { FeaturedPark } from '../types';

/**
 * "National Parks" landing-page rail: up to 12 core-watchlist parks with a
 * live bookability badge. Renders nothing until data arrives (no skeleton).
 */
export function FeaturedParks() {
  const { data: parks } = useFeaturedParks();

  if (!parks || parks.length === 0) return null;

  return (
    <section className="mb-16">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">National Parks</h2>
          <p className="text-gray-600 mt-1">
            Marquee parks sell out months ahead — set an alert and we'll catch
            cancellations the moment they open.
          </p>
        </div>
        <Link
          to="/discovery"
          className="text-sm font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap"
        >
          View all parks &rarr;
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {parks.slice(0, 12).map((park) => (
          <ParkCard key={park.placeId} park={park} />
        ))}
      </div>
    </section>
  );
}

// Same thresholds as BookabilityPanel: ≥0.97 red, ≥0.8 amber, else emerald.
function badgeDotClass(pct: number): string {
  if (pct >= 0.97) return 'bg-red-500';
  if (pct >= 0.8) return 'bg-amber-500';
  return 'bg-emerald-500';
}

function ParkCard({ park }: { park: FeaturedPark }) {
  return (
    <Link
      to="/park/$id"
      params={{ id: park.placeId }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group"
    >
      <div className="relative h-36 overflow-hidden">
        {park.heroImageUrl ? (
          <img
            src={park.heroImageUrl}
            alt={park.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full bg-gray-100" />
        )}
        {park.bookedPct !== null && (
          <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg text-xs font-semibold tabular-nums flex items-center gap-1.5">
            <span
              className={`w-2 h-2 rounded-full ${badgeDotClass(park.bookedPct)}`}
            />
            {Math.round(park.bookedPct * 100)}% booked
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-gray-900 line-clamp-1">{park.name}</h3>
        <p className="text-sm text-gray-500 mt-1">
          {park.facilityCount} campground{park.facilityCount === 1 ? '' : 's'}
        </p>
        <p className="text-xs text-emerald-700 font-medium flex items-center gap-1 mt-1.5">
          <Bell className="w-3 h-3" />
          Get open-site alerts
        </p>
      </div>
    </Link>
  );
}
